/**
 * What the referrer layout leaves out of the reading workspace (pure; see test/referrer-layout.test.mjs).
 *
 * A referring doctor or convenio user opens an exam from the results portal: they read it (W/L, zoom,
 * pan, measures, cine, sync, layouts, series browser, the other studies on the ticket) but take nothing
 * away and change nothing in ris. Left out twice over, so one slip does not reopen a path:
 *   - the modules that export, share, run the assistant or write to ris are never installed;
 *   - the commands behind those features are refused by the command gate (menus hide them, the
 *     keyboard and any leftover button get "no permission").
 */

/** Workspace modules (by their name in src/main.ts) the referrer layout never installs. */
export const REFERRER_LEFT_OUT_MODULES = ['ris', 'keyImages', 'share', 'assistant'] as const;

/** Where the referrer module takes the place of the ris workspace module (after the toolbar). */
const REPLACED_MODULE = 'ris';

/**
 * Command kinds (the part before `:`) the referrer layout refuses: copy and image download (export),
 * secondary capture / PDF attach / settings / key images (writes to ris), DICOM download (export), the
 * report and "Compartir" (served elsewhere or not at all), extra screens (unwatermarked windows) and AI.
 */
export const REFERRER_BLOCKED_COMMANDS: ReadonlySet<string> = new Set([
  'copy-image',
  'copy-grid',
  'download-dialog',
  'download-dicom',
  'secondary-capture',
  'attach-pdf',
  'settings',
  'reset-config',
  'key-images',
  'keyimage',
  'study-report',
  'share-study',
  'multi-screen',
  'ai-module',
]);

export function referrerAllowsCommand(id: string): boolean {
  return !REFERRER_BLOCKED_COMMANDS.has(id.split(':')[0]);
}

/**
 * The referrer's module list: the workspace in its order, without the left-out modules, with `own`
 * (src/referrer: branding + watermark) where the ris module was, or last when there is none.
 */
export function referrerModuleList<T>(workspace: ReadonlyArray<readonly [string, T]>, own: T): T[] {
  const leftOut = new Set<string>(REFERRER_LEFT_OUT_MODULES);
  const modules: T[] = [];
  let placed = false;
  for (const [name, module] of workspace) {
    if (name === REPLACED_MODULE) {
      modules.push(own);
      placed = true;
    }
    if (!leftOut.has(name)) modules.push(module);
  }
  if (!placed) modules.push(own);
  return modules;
}

/** Shown when ris sent no watermark text (it always should for a referrer ticket). */
export const WATERMARK_FALLBACK = 'Solo para consulta médica';

/** The watermark ris set, else the fallback with the org name when there is one. */
export function watermarkText(watermark: string | null | undefined, orgName: string | null | undefined): string {
  const text = watermark?.trim();
  if (text) return text;
  const org = orgName?.trim();
  return org ? `${WATERMARK_FALLBACK} · ${org}` : WATERMARK_FALLBACK;
}

const XML_ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' };
const FONT_PX = 15;
/** Average glyph width of the sans-serif fallback at FONT_PX, to size the tile around the text. */
const GLYPH_PX = 8;
const TILE_PADDING_PX = 72;
const MIN_TILE_WIDTH_PX = 240;
const MIN_ROW_HEIGHT_PX = 150;
/** Rotated by 30°, a line of length L rises L·sin 30° = L/2: each row is at least that tall plus a margin. */
const ROW_MARGIN_PX = 60;
const ANGLE_DEG = -30;

export function escapeXml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => XML_ESCAPES[c]);
}

/**
 * One tile of the diagonal watermark as an SVG (repeated by CSS as a background, so it covers any
 * size and survives resizes and layout changes without redrawing). Two staggered copies per tile so
 * the rows do not line up. Light text with a dark outline reads on dark and bright images alike.
 */
export function watermarkTileSvg(text: string): string {
  const length = text.length * GLYPH_PX;
  const width = Math.max(MIN_TILE_WIDTH_PX, length + TILE_PADDING_PX);
  const row = Math.max(MIN_ROW_HEIGHT_PX, Math.ceil(length / 2) + ROW_MARGIN_PX);
  const safe = escapeXml(text);
  const line = (x: number, y: number): string =>
    `<text x="${x}" y="${y}" dominant-baseline="middle" transform="rotate(${ANGLE_DEG} ${x} ${y})">${safe}</text>`;
  // The edge copies (x = 0 and x = width) are halves that the next tile completes.
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${row * 2}">`
    + `<g font-family="sans-serif" font-size="${FONT_PX}" font-weight="600" text-anchor="middle" fill="#fff" fill-opacity="0.24" `
    + `stroke="#000" stroke-opacity="0.24" stroke-width="2" paint-order="stroke">`
    + line(width / 2, row / 2)
    + line(0, row * 1.5)
    + line(width, row * 1.5)
    + `</g></svg>`;
}

/** The tile as a CSS `url(...)` value. */
export function watermarkBackground(text: string): string {
  return `url("data:image/svg+xml,${encodeURIComponent(watermarkTileSvg(text))}")`;
}
